
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { countriesCode } from "../../Validations/countryCodeData";
import common from "../../../utils/common";
const sizeClasses = {
  xs: "px-2 py-0",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const countries = countriesCode;

const topCountries = [
  { code: "+1", flag: "🇺🇸", name: "United States" },
  { code: "+91", flag: "🇮🇳", name: "India" },
  { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+43", flag: "🇦🇹", name: "Austria" },
];

const allCountries = [
  ...topCountries,
  ...countries.filter(
    (country) =>
      !topCountries.some((topCountry) => topCountry.code === country.code)
  ),
];

const CustomPhoneInput = ({
  value = "",
  onChange,
  placeholder = "Enter phone number...",
  size = "md",
  rounded = "rounded",
  selectedCountryCode = "+1",
  disabled = false,
  shadow = false,
}) => {
  const { validatePhoneNumber } = common;
  const [selectedCountry, setSelectedCountry] = useState(() => {
    return allCountries.find((c) => c.code === selectedCountryCode) || countries[0];
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // NEW: track dropdown position for portal rendering
  const [dropdownStyle, setDropdownStyle] = useState({});

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const portalRef = useRef(null);

  // Create a stable portal container
  useEffect(() => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    portalRef.current = el;
    return () => document.body.removeChild(el);
  }, []);

  // Close on outside click (must check both button and portal dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideButton = buttonRef.current?.contains(event.target);
      const clickedInsidePortal = portalRef.current?.contains(event.target);
      if (!clickedInsideButton && !clickedInsidePortal) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Recalculate dropdown position whenever it opens or window scrolls/resizes
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300;

      // Open upward if not enough space below
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        width: Math.max(rect.width, 220),
        zIndex: 9999,
        ...(openUpward
          ? { bottom: windowHeight - rect.top, top: "auto" }
          : { top: rect.bottom + 4, bottom: "auto" }),
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const handleCountrySelect = (country) => {
    const stringValue = String(value || "");
    const numberOnly = stringValue.replace(selectedCountry.code, "");
    setSelectedCountry(country);
    const newValue = country.code + numberOnly;
    onChange(newValue, {
      countryCode: country.code,
      number: numberOnly,
      fullNumber: newValue,
      flag: country.flag, //mahee
      error: validatePhoneNumber(`${country.code}${numberOnly}`, country.flag),
    });

    setIsOpen(false);
    setSearchQuery("");
  };

  // Ensure value is a string
  const phoneValue = String(value || "");

  const handlePhoneChange = (e) => {
    const phoneNumber = e.target.value.replace(/\D/g, "");
    const limitedNumber = phoneNumber.slice(0, 18);
    const newValue = selectedCountry.code + limitedNumber;
    onChange(newValue, {
      countryCode: selectedCountry.code,
      number: limitedNumber,
      fullNumber: newValue,
      flag: selectedCountry.flag, //mahee
      error: validatePhoneNumber(
        `${selectedCountry.code}${limitedNumber}`,
        selectedCountry.flag
      ),
    });
  };
  const filteredCountries = allCountries.filter((country) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      country.name.toLowerCase().includes(searchLower) ||
      country.code.includes(searchQuery) ||
      country.flag.includes(searchQuery)
    );
  });

  // Add this useEffect to update selectedCountry when selectedCountryCode changes
  useEffect(() => {
    const country =
      allCountries.find((c) => c.code === selectedCountryCode) || countries[0];
    setSelectedCountry(country);
  }, [selectedCountryCode]);

  const dropdownMaxHeight = 300;

  const dropdownMenu = isOpen && portalRef.current
    ? createPortal(
        <div
          className="bg-white border border-gray-300 rounded-md shadow-lg whitespace-nowrap overflow-hidden"
          style={{ ...dropdownStyle, maxHeight: dropdownMaxHeight }}
        >
          <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search country..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary"
            />
          </div>
          <div
            className="overflow-y-auto"
            style={{ maxHeight: dropdownMaxHeight - 53 }}
          >
            {filteredCountries.map((country) => (
              <button
                key={country.code}
                type="button"
                onMouseDown={(e) => {
                  // Use onMouseDown to fire before onBlur closes things
                  e.preventDefault();
                  handleCountrySelect(country);
                }}
                className="flex items-center w-full gap-2 px-3 py-2 text-left hover:bg-gray-100"
              >
                <span>{country.flag}</span>
                <span>{country.code}</span>
                <span className="text-sm text-gray-600">{country.name}</span>
              </button>
            ))}
            {filteredCountries.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No countries found
              </div>
            )}
          </div>
        </div>,
        portalRef.current
      )
    : null;

  return (
    <div
      className={`flex-1 relative flex rounded border focus:outline-none w-full ${
        isFocused ? "border-primary outline-none" : ""
      } 
      ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
      ${
        disabled
          ? "border-neutrals-200 bg-neutrals-200 text-black cursor-not-allowed"
          : "hover:border-primary focus:border-primary"
      }`}
    >
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setIsFocused(false);
            }
          }}
          disabled={disabled}
          className={`flex items-center justify-between border-0  ${
            sizeClasses[size]
          } ${
            fontSizeClasses[size]
          } ${rounded}  rounded-r-none border-r-0 w-[80px] 
          ${
            disabled
              ? "bg-neutrals-200 text-black !cursor-not-allowed"
              : "hover:border-primary focus:border-primary"
          }`}
        >
          <span className="flex items-center justify-center gap-1 flex-1">
            {/* <span>{selectedCountry.flag}</span> */}
            <span>{selectedCountry.code}</span>
          </span>
          <FaChevronDown className="ml-1 text-gray-400" />
        </button>

        {/* Portal-rendered dropdown — lives outside all overflow containers */}
        {dropdownMenu}
      </div>
      <input
        type="tel"
        value={phoneValue.replace(selectedCountry.code, "")}
        onChange={handlePhoneChange}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          if (!dropdownRef.current?.contains(e.relatedTarget)) {
            setIsFocused(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={`flex-1 pl-0 border-0 focus:outline-none w-full ${
          sizeClasses[size]
        } ${fontSizeClasses[size]} ${rounded} rounded-l-none
        ${
          disabled
            ? "bg-neutrals-200 text-black cursor-not-allowed"
            : "hover:border-primary focus:border-primary"
        }`}
      />
    </div>
  );
};

export default CustomPhoneInput;



//it is commented because its dropdown is going inside the modal 
// import React, { useEffect, useRef, useState } from "react";
// import { FaChevronDown } from "react-icons/fa";
// import { countriesCode } from "../../Validations/countryCodeData";
// import common from "../../../utils/common";
// const sizeClasses = {
//   xs: "px-2 py-0",
//   sm: "px-3 py-1.5",
//   md: "px-3 py-2",
//   lg: "px-4 py-2.5",
//   xl: "px-5 py-3",
// };

// const fontSizeClasses = {
//   xs: "text-[12px]",
//   sm: "text-[12px]",
//   md: "text-[14px]",
//   lg: "text-[16px]",
//   xl: "text-[18px]",
// };

// const countries = countriesCode;

// const topCountries = [
//   { code: "+1", flag: "🇺🇸", name: "United States" },
//   { code: "+91", flag: "🇮🇳", name: "India" },
//   { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
//   { code: "+43", flag: "🇦🇹", name: "Austria" },
// ];

// const allCountries = [
//   ...topCountries,
//   ...countries.filter(
//     (country) =>
//       !topCountries.some((topCountry) => topCountry.code === country.code)
//   ),
// ];

// const CustomPhoneInput = ({
//   value = "",
//   onChange,
//   placeholder = "Enter phone number...",
//   size = "md",
//   rounded = "rounded",
//   selectedCountryCode = "+1",
//   disabled = false,
//   shadow = false,
// }) => {
//   const { validatePhoneNumber } = common;
//   const [selectedCountry, setSelectedCountry] = useState(() => {
//     const country =
//       allCountries.find((c) => c.code === selectedCountryCode) || countries[0];
//     return country;
//   });

//   const [isOpen, setIsOpen] = useState(false);
//   const [isFocused, setIsFocused] = useState(false);
//   const [dropdownMaxHeight, setDropdownMaxHeight] = useState(300);
//   const [searchQuery, setSearchQuery] = useState("");
//   const dropdownRef = useRef(null);
//   const buttonRef = useRef(null);
//   const searchInputRef = useRef(null);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsOpen(false);
//         setSearchQuery("");
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isOpen && buttonRef.current) {
//       const buttonRect = buttonRef.current.getBoundingClientRect();
//       const windowHeight = window.innerHeight;
//       const spaceBelow = windowHeight - buttonRect.bottom;
//       const minHeight = 150;
//       const maxHeight = Math.max(minHeight, Math.min(300, spaceBelow - 10));
//       setDropdownMaxHeight(maxHeight);
//       if (searchInputRef.current) {
//         searchInputRef.current.focus();
//       }
//     }
//   }, [isOpen]);

//   const handleCountrySelect = (country) => {
//     const stringValue = String(value || "");
//     const numberOnly = stringValue.replace(selectedCountry.code, "");
//     setSelectedCountry(country);
//     const newValue = country.code + numberOnly;
//     onChange(newValue, {
//       countryCode: country.code,
//       number: numberOnly,
//       fullNumber: newValue,
//       flag: country.flag, //mahee
//       error: validatePhoneNumber(`${country.code}${numberOnly}`, country.flag),
//     });

//     setIsOpen(false);
//     setSearchQuery("");
//   };

//   // Ensure value is a string
//   const phoneValue = String(value || "");

//   const handlePhoneChange = (e) => {
//     const phoneNumber = e.target.value.replace(/\D/g, "");
//     const limitedNumber = phoneNumber.slice(0, 18);
//     const newValue = selectedCountry.code + limitedNumber;
//     onChange(newValue, {
//       countryCode: selectedCountry.code,
//       number: limitedNumber,
//       fullNumber: newValue,
//       flag: selectedCountry.flag, //mahee
//       error: validatePhoneNumber(
//         `${selectedCountry.code}${limitedNumber}`,
//         selectedCountry.flag
//       ),
//     });
//   };
//   const filteredCountries = allCountries.filter((country) => {
//     const searchLower = searchQuery.toLowerCase();
//     return (
//       country.name.toLowerCase().includes(searchLower) ||
//       country.code.includes(searchQuery) ||
//       country.flag.includes(searchQuery)
//     );
//   });

//   // Add this useEffect to update selectedCountry when selectedCountryCode changes
//   useEffect(() => {
//     const country =
//       allCountries.find((c) => c.code === selectedCountryCode) || countries[0];
//     setSelectedCountry(country);
//   }, [selectedCountryCode]);

//   return (
//     <div
//       className={`flex-1 relative flex rounded border focus:outline-none w-full ${
//         isFocused ? "border-primary outline-none" : ""
//       } 
//       ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
//       ${
//         disabled
//           ? "border-neutrals-200 bg-neutrals-200 text-black cursor-not-allowed"
//           : "hover:border-primary focus:border-primary"
//       }`}
//     >
//       <div className="relative" ref={dropdownRef}>
//         <button
//           ref={buttonRef}
//           type="button"
//           onClick={() => setIsOpen(!isOpen)}
//           onFocus={() => setIsFocused(true)}
//           onBlur={(e) => {
//             if (!e.currentTarget.contains(e.relatedTarget)) {
//               setIsFocused(false);
//             }
//           }}
//           disabled={disabled}
//           className={`flex items-center justify-between border-0  ${
//             sizeClasses[size]
//           } ${
//             fontSizeClasses[size]
//           } ${rounded}  rounded-r-none border-r-0 w-[80px] 
//           ${
//             disabled
//               ? "bg-neutrals-200 text-black !cursor-not-allowed"
//               : "hover:border-primary focus:border-primary"
//           }`}
//         >
//           <span className="flex items-center justify-center gap-1 flex-1">
//             {/* <span>{selectedCountry.flag}</span> */}
//             <span>{selectedCountry.code}</span>
//           </span>
//           <FaChevronDown className="ml-1 text-gray-400" />
//         </button>
//         {isOpen && (
//           <div
//             className="list absolute z-10 min-w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg whitespace-nowrap"
//             style={{ maxHeight: `${dropdownMaxHeight}px` }}
//           >
//             <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
//               <input
//                 ref={searchInputRef}
//                 type="text"
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//                 placeholder="Search country..."
//                 className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary"
//               />
//             </div>
//             <div
//               className="overflow-y-auto"
//               style={{ maxHeight: `${dropdownMaxHeight - 53}px` }}
//             >
//               {filteredCountries.map((country) => (
//                 <button
//                   key={country.code}
//                   type="button"
//                   onClick={() => handleCountrySelect(country)}
//                   className="flex items-center w-full gap-2 px-3 py-2 text-left hover:bg-gray-100"
//                 >
//                   <span>{country.flag}</span>
//                   <span>{country.code}</span>
//                   <span className="text-sm text-gray-600">{country.name}</span>
//                 </button>
//               ))}
//               {filteredCountries.length === 0 && (
//                 <div className="px-3 py-2 text-sm text-gray-500">
//                   No countries found
//                 </div>
//               )}
//             </div>
//           </div>
//         )}
//       </div>
//       <input
//         type="tel"
//         value={phoneValue.replace(selectedCountry.code, "")}
//         onChange={handlePhoneChange}
//         onFocus={() => setIsFocused(true)}
//         onBlur={(e) => {
//           if (!dropdownRef.current?.contains(e.relatedTarget)) {
//             setIsFocused(false);
//           }
//         }}
//         placeholder={placeholder}
//         disabled={disabled}
//         className={`flex-1 pl-0 border-0 focus:outline-none w-full ${
//           sizeClasses[size]
//         } ${fontSizeClasses[size]} ${rounded} rounded-l-none
//         ${
//           disabled
//             ? "bg-neutrals-200 text-black cursor-not-allowed"
//             : "hover:border-primary focus:border-primary"
//         }`}
//       />
//     </div>
//   );
// };

// export default CustomPhoneInput;
